import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Images, Upload, Plus, X, ArrowLeft, ArrowRight, Star, Crop } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  GET_OWNER_MEDIA,
  GET_MEDIA_ASSETS,
  LINK_MEDIA,
  UNLINK_MEDIA,
  SET_PRIMARY_MEDIA,
} from '../../graphql';
import {
  fetchAssetBytes,
  imageDimensions,
  saveRendition,
  uploadAsset,
} from '../../lib/media/uploadAsset';
import { ensureOpenCv } from '../../lib/capture/vendor';
import { Loader } from '../common/PageLoader';

/**
 * Images attached to one content item.
 *
 * Reads through `ownerMedia` rather than topics.media_items: media_items is a
 * denormalized cache whose shape differs by ingest path (RSS writes
 * { images, videos }, WordPress and the media layer write a flat array), while
 * ownerMedia resolves from media_links and always reports the rendition that
 * should be served right now.
 *
 * Attaching is slot-based — role plus position — so re-shooting the front of a
 * card replaces that slot rather than appending a second front.
 */

const ROLES = ['front', 'back', 'other', 'featured', 'attachment'] as const;
type Role = (typeof ROLES)[number];

interface OwnerMediaItem {
  assetId: string;
  role: string;
  position: number;
  isPrimary: boolean;
  altText: string | null;
  url: string | null;
  width: number | null;
  height: number | null;
  servingKind: string | null;
}

interface LibraryAsset {
  id: string;
  originalFilename: string | null;
  sha256: string;
  renditions: { kind: string; url: string | null; isCurrent: boolean }[];
}

function thumbOf(a: LibraryAsset): string | null {
  const cur = a.renditions?.filter((r) => r.isCurrent) ?? [];
  return (
    cur.find((r) => r.kind === 'thumb')?.url ??
    cur.find((r) => r.kind === 'web')?.url ??
    cur.find((r) => r.kind === 'canonical')?.url ??
    cur.find((r) => r.kind === 'original')?.url ??
    null
  );
}

export function TopicMediaPanel({
  accountId,
  topicId,
  defaultRole = 'featured',
  onChanged,
}: {
  accountId: string;
  topicId: string;
  /** Cards lead with `front`; articles lead with `featured`. */
  defaultRole?: Role;
  onChanged?: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const { data, refetch } = useQuery(GET_OWNER_MEDIA, {
    variables: { accountId, ownerType: 'topic', ownerId: topicId },
    skip: !accountId || !topicId,
    fetchPolicy: 'cache-and-network',
  });

  const [linkMedia] = useMutation(LINK_MEDIA);
  const [unlinkMedia] = useMutation(UNLINK_MEDIA);
  const [setPrimaryMedia] = useMutation(SET_PRIMARY_MEDIA);

  const attached: OwnerMediaItem[] = useMemo(() => data?.ownerMedia ?? [], [data]);

  const refresh = useCallback(async () => {
    await refetch();
    onChanged?.();
  }, [refetch, onChanged]);

  /** Next free position within a role, so attaching never collides a slot. */
  const nextPosition = (role: string) => {
    const used = attached.filter((m) => m.role === role).map((m) => m.position);
    let p = 0;
    while (used.includes(p)) p++;
    return p;
  };

  const attach = async (assetId: string, role: Role) => {
    setBusy('Attaching…');
    try {
      await linkMedia({
        variables: {
          accountId,
          input: {
            assetId,
            ownerType: 'topic',
            ownerId: topicId,
            role,
            position: nextPosition(role),
          },
        },
      });
      await refresh();
      setPicking(false);
    } finally {
      setBusy('');
    }
  };

  /**
   * Mark one image as the item's display image. Clicking the current choice
   * clears it, returning the item to role/position inference.
   */
  const choosePrimary = async (m: OwnerMediaItem) => {
    setBusy('Setting main image…');
    try {
      await setPrimaryMedia({
        variables: {
          accountId,
          ownerType: 'topic',
          ownerId: topicId,
          assetId: m.isPrimary ? null : m.assetId,
        },
      });
      await refresh();
    } finally {
      setBusy('');
    }
  };

  const detach = async (m: OwnerMediaItem) => {
    setBusy('Removing…');
    try {
      await unlinkMedia({
        variables: {
          accountId,
          ownerType: 'topic',
          ownerId: topicId,
          role: m.role,
          position: m.position,
        },
      });
      await refresh();
    } finally {
      setBusy('');
    }
  };

  /**
   * Change an image's role or position.
   *
   * Unlink-then-link rather than an update mutation, because the slot IS the
   * key. Order matters: releasing the old slot first means moving an image into
   * a slot it already partly occupies cannot collide with itself.
   */
  const move = async (m: OwnerMediaItem, role: Role, position: number) => {
    if (m.role === role && m.position === position) return;
    setBusy('Moving…');
    try {
      await unlinkMedia({
        variables: {
          accountId,
          ownerType: 'topic',
          ownerId: topicId,
          role: m.role,
          position: m.position,
        },
      });
      await linkMedia({
        variables: {
          accountId,
          input: {
            assetId: m.assetId,
            ownerType: 'topic',
            ownerId: topicId,
            role,
            position,
            altText: m.altText,
          },
        },
      });
      await refresh();
    } finally {
      setBusy('');
    }
  };

  /**
   * Re-crop an attached image.
   *
   * Always sources the ORIGINAL, never what is on screen — cropping a crop
   * compounds, eating a little more of the card each pass, and the original is
   * immutable precisely so this can be repeated. The result is stored as a new
   * `canonical` rendition which supersedes the previous one, so every earlier
   * version is still there and restorable from the media library's history.
   *
   * The cropper is the one ported from the capture app: OpenCV quad detection
   * with manual corner handles, and the mat/white-balance profile the photo was
   * shot under is handed to it so auto-detection keys on the right background.
   */
  const recrop = async (m: OwnerMediaItem) => {
    setBusy('Loading the cropper…');
    try {
      const [{ blob, captureMeta }, cvReady] = await Promise.all([
        fetchAssetBytes(accountId, m.assetId, 'original'),
        ensureOpenCv(),
      ]);
      const { openCropper } = await import('../../lib/capture/crop.js');
      setBusy('');

      const cropped: Blob | null = await openCropper({
        blob,
        title: cvReady ? `Crop ${m.role}` : `Crop ${m.role} — manual (OpenCV unavailable)`,
      });
      if (!cropped) return;

      setBusy('Saving the crop…');
      const dims = await imageDimensions(cropped);
      await saveRendition(accountId, m.assetId, cropped, {
        kind: 'canonical',
        width: dims.width,
        height: dims.height,
        // Enough provenance to explain the bytes later: what produced them,
        // from what, and under which calibration.
        transform: {
          op: 'crop',
          source: 'original',
          by: 'website',
          at: new Date().toISOString(),
          captureMeta: captureMeta ?? null,
        },
      });
      await refresh();
      onChanged?.();
      setBusy('');
    } catch (err) {
      setBusy(err instanceof Error ? err.message : 'Crop failed');
    }
  };

  const uploadAndAttach = async (files: FileList | null) => {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      setBusy(`Uploading ${file.name}…`);
      try {
        // The probe inside uploadAsset skips the transfer when the library
        // already holds these bytes; either way we get an asset id to attach.
        const uploaded = await uploadAsset(accountId, file, { filename: file.name });
        await attach(uploaded.assetId, defaultRole);
      } catch (err) {
        setBusy(err instanceof Error ? err.message : 'Upload failed');
        return;
      }
    }
    setBusy('');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-cmp-ink flex items-center gap-2">
          <Images className="w-4 h-4" /> Images
          {attached.length > 0 && <Badge variant="outline">{attached.length}</Badge>}
        </h3>
        <div className="flex items-center gap-2">
          {busy && <Loader size="inline" label={busy} className="text-xs text-cmp-ink-2" />}
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              void uploadAndAttach(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={() => fileInput.current?.click()}
          >
            <Upload className="w-3 h-3" /> Upload
          </Button>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setPicking(true)}>
            <Plus className="w-3 h-3" /> From library
          </Button>
        </div>
      </div>

      {attached.length === 0 && (
        <p className="text-xs text-cmp-ink-2">
          No images attached. Upload one, or pick from the media library.
        </p>
      )}

      {attached.length > 0 && !attached.some((m) => m.isPrimary) && (
        <p className="text-xs text-cmp-ink-2">
          No main image chosen — the front (or featured) image is used by default. Tick one to
          override.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {attached.map((m) => (
          <Card
            key={`${m.role}-${m.position}`}
            className="overflow-hidden group bg-cmp-raise border border-cmp-line text-cmp-ink"
          >
            <div className="aspect-[3/4] bg-cmp-surface flex items-center justify-center overflow-hidden">
              {m.url ? (
                <img
                  src={m.url}
                  alt={m.altText || m.role}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Images className="w-5 h-5 opacity-40" />
              )}
            </div>
            <div className="p-2 space-y-1">
              <label
                className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none"
                title="Use this as the item's main image"
              >
                <input
                  type="checkbox"
                  checked={m.isPrimary}
                  onChange={() => void choosePrimary(m)}
                  className="accent-[var(--cmp-bronze)]"
                />
                <span
                  className={
                    m.isPrimary ? 'text-cmp-ink flex items-center gap-1' : 'text-cmp-ink-2'
                  }
                >
                  {m.isPrimary && <Star className="w-3 h-3 fill-current" />}
                  Main image
                </span>
              </label>
              <select
                value={m.role}
                onChange={(e) => void move(m, e.target.value as Role, nextPosition(e.target.value))}
                className="w-full text-[11px] bg-transparent border border-cmp-line rounded px-1 py-0.5 text-cmp-ink"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button
                onClick={() => void recrop(m)}
                title="Crop this image (the original is kept)"
                className="w-full text-[11px] text-cmp-ink-2 hover:text-cmp-ink inline-flex items-center justify-center gap-1 py-0.5"
              >
                <Crop className="w-3 h-3" /> crop
              </button>
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5">
                  <button
                    title="Move earlier"
                    disabled={m.position === 0}
                    onClick={() => void move(m, m.role as Role, m.position - 1)}
                    className="p-1 text-cmp-ink-3 hover:text-cmp-ink disabled:opacity-30"
                  >
                    <ArrowLeft className="w-3 h-3" />
                  </button>
                  <button
                    title="Move later"
                    onClick={() => void move(m, m.role as Role, m.position + 1)}
                    className="p-1 text-cmp-ink-3 hover:text-cmp-ink"
                  >
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                <button
                  title="Detach"
                  onClick={() => void detach(m)}
                  className="p-1 text-cmp-ink-3 hover:text-red-400"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {picking && (
        <LibraryPicker
          accountId={accountId}
          defaultRole={defaultRole}
          onPick={attach}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

/** Modal grid of the account's media, for attaching an existing asset. */
function LibraryPicker({
  accountId,
  defaultRole,
  onPick,
  onClose,
}: {
  accountId: string;
  defaultRole: Role;
  onPick: (assetId: string, role: Role) => void | Promise<void>;
  onClose: () => void;
}) {
  const [role, setRole] = useState<Role>(defaultRole);
  const [filter, setFilter] = useState('');
  const { data, loading } = useQuery(GET_MEDIA_ASSETS, {
    variables: { accountId, limit: 60, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });

  const assets: LibraryAsset[] = data?.mediaAssets?.assets ?? [];
  const shown = filter
    ? assets.filter((a) => (a.originalFilename || '').toLowerCase().includes(filter.toLowerCase()))
    : assets;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
      <Card className="w-full max-w-4xl max-h-[80vh] overflow-auto p-5 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink shadow-2xl">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-cmp-ink">Attach from library</h3>
          <div className="flex items-center gap-2">
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by filename…"
              className="w-56"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="text-sm bg-transparent border border-cmp-line rounded px-2 py-1 text-cmp-ink"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  as {r}
                </option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading && <Loader label="Loading library" className="text-sm text-cmp-ink-2" />}
        {!loading && shown.length === 0 && (
          <div className="text-sm text-cmp-ink-2">Nothing in the library matches.</div>
        )}

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {shown.map((a) => {
            const url = thumbOf(a);
            return (
              <button
                key={a.id}
                onClick={() => void onPick(a.id, role)}
                className="text-left group"
                title={a.originalFilename || a.sha256.slice(0, 12)}
              >
                <div className="aspect-[3/4] bg-cmp-surface rounded overflow-hidden flex items-center justify-center group-hover:ring-1 group-hover:ring-cmp-bronze">
                  {url ? (
                    <img src={url} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <Images className="w-5 h-5 opacity-40" />
                  )}
                </div>
                <div className="text-[11px] text-cmp-ink-2 truncate mt-1">
                  {a.originalFilename || a.sha256.slice(0, 10)}
                </div>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
